import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Phase = "inhale" | "hold1" | "exhale" | "hold2";
const ORDER: Phase[] = ["inhale", "hold1", "exhale", "hold2"];

export interface BreathingPattern { inhale: number; hold1: number; exhale: number; hold2: number; cycles?: number; }

/** In-app breathing pacer: a circle that expands on inhale, holds, contracts on
 *  exhale. Respects prefers-reduced-motion. No dependencies. */
export default function BreathingCircle({ inhale = 4, hold1 = 4, exhale = 4, hold2 = 4, cycles = 6, onComplete }:
  BreathingPattern & { onComplete?: () => void }) {
  const { t } = useTranslation();
  const pattern: Record<Phase, number> = { inhale, hold1, exhale, hold2 };
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<Phase>("inhale");
  const [count, setCount] = useState(inhale);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const reduce = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clear(), []);

  const runPhase = (i: number, cyc: number) => {
    if (cyc >= cycles) { setRunning(false); onComplete?.(); return; }
    const p = ORDER[i];
    const secs = pattern[p];
    if (secs <= 0) { advance(i, cyc); return; }
    setPhase(p); setCount(secs);
    for (let s = 1; s < secs; s++) timers.current.push(setTimeout(() => setCount(secs - s), s * 1000));
    timers.current.push(setTimeout(() => advance(i, cyc), secs * 1000));
  };
  const advance = (i: number, cyc: number) => { const n = (i + 1) % 4; runPhase(n, n === 0 ? cyc + 1 : cyc); };
  const start = () => { clear(); setRunning(true); runPhase(0, 0); };
  const stop = () => { clear(); setRunning(false); };

  const big = phase === "inhale" || phase === "hold1";
  const dur = phase === "inhale" ? inhale : phase === "exhale" ? exhale : 0.3;
  const labelKey = `breathing.${phase}`;

  return (
    <div style={{ textAlign: "center", padding: 24, margin: "12px 0", borderRadius: 16, background: "var(--primary-50, #EEF4F1)" }}>
      <div style={{ position: "relative", width: 240, height: 240, margin: "0 auto", display: "grid", placeItems: "center" }}>
        <div aria-hidden style={{
          width: 200, height: 200, borderRadius: "50%",
          background: "radial-gradient(circle at 50% 40%, var(--primary-300,#7FA897), var(--primary-500,#3C6B57))",
          transform: reduce ? "scale(0.8)" : `scale(${running ? (big ? 1 : 0.55) : 0.7})`,
          transition: reduce ? "none" : `transform ${dur}s ease-in-out`,
          boxShadow: "0 12px 40px rgba(0,0,0,.12)",
        }} />
        <div style={{ position: "absolute", color: "#fff", fontWeight: 600 }} aria-live="polite">
          <div style={{ fontSize: "1.1rem" }}>{running ? t(labelKey) : t("breathing.ready")}</div>
          {running && <div style={{ fontSize: "2rem" }}>{count}</div>}
        </div>
      </div>
      <button type="button" onClick={running ? stop : start} style={{
        marginTop: 18, padding: "10px 24px", borderRadius: 10, border: "none",
        background: "var(--primary-500,#3C6B57)", color: "#fff", fontWeight: 600, cursor: "pointer",
      }}>
        {running ? t("breathing.stop") : t("breathing.start")}
      </button>
    </div>
  );
}
